<div class="document-preview-dialog-wrapper" [class.is-fullscreen]="isFullScreen">
  <!-- Dialog Header -->
  <div class="preview-modal-header d-flex align-items-center justify-content-between px-3 py-2">
    <!-- Left Info: Format Badge, Title, Index Counter -->
    <div class="d-flex align-items-center gap-2 overflow-hidden me-2" style="min-width: 0;">
      <span class="file-type-tag" [ngClass]="'tag-' + normalizedFormat.toLowerCase()">
        {{ normalizedFormat || 'FILE' }}
      </span>

      <div class="d-flex flex-column text-truncate" style="min-width: 0;">
        <span class="doc-file-name text-white font-weight-bold text-truncate" [matTooltip]="activeDoc?.fileName || ''"
          [title]="activeDoc?.fileName || ''" matTooltipPosition="above">
          {{ activeDoc?.fileName }}
        </span>
        <span class="doc-meta-subtext text-white fs-8">
          <span *ngIf="ticketId">{{ ticketId }}</span>
        </span>
      </div>

      <span class="doc-counter-badge ms-1" *ngIf="attachments.length > 1">
        {{ currentIndex + 1 }} / {{ attachments.length }}
      </span>
    </div>

    <!-- Toolbar & Actions -->
    <div class="d-flex align-items-center gap-2">
      <!-- Image Zoom & Rotate Controls -->
      <div class="preview-toolbar d-flex align-items-center gap-1" *ngIf="isImage">
        <button type="button" class="toolbar-btn" (click)="zoomOut()" [disabled]="zoomScale <= 0.5"
          title="Zoom Out (-)">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            <line x1="8" y1="11" x2="14" y2="11"></line>
          </svg>
        </button>

        <span class="zoom-value">{{ (zoomScale * 100).toFixed(0) }}%</span>

        <button type="button" class="toolbar-btn" (click)="zoomIn()" [disabled]="zoomScale >= 3.0" title="Zoom In (+)">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            <line x1="11" y1="8" x2="11" y2="14"></line>
            <line x1="8" y1="11" x2="14" y2="11"></line>
          </svg>
        </button>

        <button type="button" class="toolbar-btn" (click)="resetZoom()" title="Reset Zoom & Rotation">
          1:1
        </button>

        <span class="toolbar-divider"></span>

        <button type="button" class="toolbar-btn" (click)="rotateLeft()" title="Rotate Left 90°">
          ↺
        </button>
        <button type="button" class="toolbar-btn" (click)="rotateRight()" title="Rotate Right 90°">
          ↻
        </button>
      </div>

      <!-- Action Buttons -->
      <div class="d-flex align-items-center gap-2 ms-2 border-start-dark ps-2">
        <button type="button" class="action-icon-btn" (click)="toggleFullScreen()"
          [title]="isFullScreen ? 'Exit Fullscreen' : 'Fullscreen'">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path *ngIf="!isFullScreen"
              d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
            <path *ngIf="isFullScreen"
              d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
          </svg>
        </button>

        <button type="button" class="action-icon-btn" (click)="openInNewTab()" title="Open in New Tab"
          *ngIf="activeDoc?.fileUrl">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
            <polyline points="15 3 21 3 21 9"></polyline>
            <line x1="10" y1="14" x2="21" y2="3"></line>
          </svg>
        </button>

        <button type="button" class="action-download-btn" (click)="downloadDocument()" title="Download File">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            class="me-1">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Download
        </button>

        <button type="button" class="btn-close-modal" (click)="close()" title="Close Viewer (Esc)">
          ✕
        </button>
      </div>
    </div>
  </div>

  <!-- Modal Main Viewport Canvas -->
  <div class="preview-modal-body position-relative">
    <!-- Navigation Arrow Left -->
    <button type="button" class="nav-arrow-btn nav-prev" (click)="prevDoc()" [disabled]="!hasPrev"
      *ngIf="attachments.length > 1" title="Previous Document (Left Arrow)">
      ‹
    </button>

    <!-- Navigation Arrow Right -->
    <button type="button" class="nav-arrow-btn nav-next" (click)="nextDoc()" [disabled]="!hasNext"
      *ngIf="attachments.length > 1" title="Next Document (Right Arrow)">
      ›
    </button>

    <!-- Loading Spinner Overlay -->
    <div class="preview-loading-overlay" *ngIf="isLoading">
      <div class="spinner-border text-success" role="status">
        <span class="visually-hidden">Loading document...</span>
      </div>
      <span class="mt-2 fs-7 text-white">Loading preview...</span>
    </div>

    <!-- PREVIEW AREA: IMAGE FORMAT (JPG, JPEG, PNG) -->
    <div class="viewport-canvas image-viewport" *ngIf="isImage && !previewUnavailable">
      <div class="image-wrapper" [style.transform]="'scale(' + zoomScale + ') rotate(' + rotationAngle + 'deg)'">
        <img *ngIf="safeImageUrl" [src]="safeImageUrl" [alt]="activeDoc?.fileName || 'Image preview'"
          (load)="onImageLoad()" (error)="onImageError()" class="img-fluid document-image-element" />
      </div>
    </div>

    <!-- PREVIEW AREA: PDF FORMAT -->
    <div class="viewport-canvas pdf-viewport" *ngIf="isPdf && !previewUnavailable">
      <iframe *ngIf="safePdfUrl" [src]="safePdfUrl" class="pdf-iframe-element" title="PDF Document Preview"
        (load)="onImageLoad()"></iframe>
    </div>

    <!-- PREVIEW AREA: ATTACHMENT FILE UNAVAILABLE (SUPPORTED FORMAT, BUT NO/BROKEN FILE URL) -->
    <div
      class="viewport-canvas unsupported-viewport d-flex flex-column align-items-center justify-content-center text-center p-4"
      *ngIf="isSupported && previewUnavailable">
      <div class="unsupported-card-box p-4 rounded-3 text-center">
        <div class="unsupported-icon-circle mx-auto mb-3">
          ⚠️
        </div>
        <h5 class="unsupported-title fw-bold mb-2">Attachment Unavailable</h5>
        <p class="unsupported-subtext fs-7 mb-3">
          This file could not be loaded. It may have been moved or removed from storage.
        </p>

        <div class="doc-file-info-badge mb-4 p-2 rounded">
          <div class="doc-file-name fw-semibold text-white fs-7">{{ activeDoc?.fileName }}</div>
          <div class="doc-file-size fs-8 mt-1">{{ activeDoc?.fileSize }}</div>
        </div>
      </div>
    </div>

    <!-- PREVIEW AREA: UNSUPPORTED FILE FORMAT (NOT PDF, JPG, JPEG, PNG) -->
    <div
      class="viewport-canvas unsupported-viewport d-flex flex-column align-items-center justify-content-center text-center p-4"
      *ngIf="!isSupported">
      <div class="unsupported-card-box p-4 rounded-3 text-center">
        <div class="unsupported-icon-circle mx-auto mb-3">
          📄
        </div>
        <h5 class="unsupported-title fw-bold mb-2">Preview Not Supported</h5>
        <p class="unsupported-subtext fs-7 mb-3">
          This viewer supports <strong class="text-white">PDF, JPG, JPEG, and PNG</strong> formats. Preview is
          unavailable for <span class="badge badge-purple px-2 py-1">{{ normalizedFormat || 'this file' }}</span>.
        </p>

        <div class="doc-file-info-badge mb-4 p-2 rounded">
          <div class="doc-file-name fw-semibold text-white fs-7">{{ activeDoc?.fileName }}</div>
          <div class="doc-file-size fs-8 mt-1">{{ activeDoc?.fileSize }}</div>
        </div>

        <button type="button"
          class="btn dialog-btn-primary w-100 py-2 d-flex align-items-center justify-content-center gap-2"
          (click)="downloadDocument()">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Download {{ activeDoc?.fileName }}
        </button>
      </div>
    </div>
  </div>

  <!-- Modal Footer / Status Bar -->
  <div class="preview-modal-footer d-flex align-items-center justify-content-between px-3 py-2 border-top-dark">
    <div class="d-flex align-items-center gap-3 text-white fs-8">
      <span>Format: <strong class="text-white">{{ normalizedFormat }}</strong></span>
      <span>Size: <strong class="text-white">{{ activeDoc?.fileSize || 'N/A' }}</strong></span>
      <span *ngIf="isImage">Zoom: <strong class="text-white">{{ (zoomScale * 100).toFixed(0) }}%</strong></span>
      <span *ngIf="isImage && rotationAngle !== 0">Rotation: <strong class="text-white">{{ rotationAngle
          }}°</strong></span>
    </div>

    <div class="d-flex align-items-center gap-2">
      <button type="button" class="btn-footer-nav" (click)="prevDoc()" [disabled]="!hasPrev"
        *ngIf="attachments.length > 1">
        ‹ Previous
      </button>
      <button type="button" class="btn-footer-nav" (click)="nextDoc()" [disabled]="!hasNext"
        *ngIf="attachments.length > 1">
        Next ›
      </button>
      <button type="button" class="btn-footer-close" (click)="close()">
        Close
      </button>
    </div>
  </div>
</div>